import { closeSiteMenu } from "./site-menu.js";
import { withBase, stripBase, SERVICES } from "./services.js";
import { parseLocalePath } from "../i18n/index.js";

const TRANSITION_DURATION_MS = 520;
const DETAIL_IMAGE_DELAY_MS = 0;
const ENTER_REVEAL_DELAY_MS = 140;
const DETAIL_TITLE_DELAY_MS = 800;
const TRANSITION_STORAGE_KEY = "magallanes-transition-color";
const TRANSITION_STORAGE_IMAGE_KEY = "magallanes-transition-image";

SERVICES.forEach((entry) => {
  const url = entry.imageUrl;
  if (!url) {
    return;
  }

  const image = new Image();
  image.decoding = "async";
  image.loading = "eager";
  image.src = url;
});

function isModifiedClick(event) {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
}

function getCurrentPath() {
  return stripBase(window.location.pathname || "/");
}

function normalizeServicePath(pathname) {
  const safe = pathname || "/";
  const withoutIndex = safe.endsWith("/index.html") ? safe.slice(0, -"/index.html".length) : safe;
  const trimmed = withoutIndex.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
}

function resolveAccentValue(variableName) {
  const value = window.getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
  return value || "#000000";
}

export function runTransition(link, onComplete) {
  const transitionLayer = document.querySelector(".page-transition");
  if (!transitionLayer) {
    onComplete();
    return;
  }

  const transitionColor = link.dataset.transitionColor || "#000000";
  const transitionImage = link.dataset.transitionImage || "";

  sessionStorage.setItem(TRANSITION_STORAGE_KEY, transitionColor);
  if (transitionImage) {
    sessionStorage.setItem(TRANSITION_STORAGE_IMAGE_KEY, transitionImage);
  } else {
    sessionStorage.removeItem(TRANSITION_STORAGE_IMAGE_KEY);
  }

  transitionLayer.style.setProperty("--transition-color", transitionColor);
  transitionLayer.style.setProperty(
    "--transition-image",
    transitionImage ? `url("${transitionImage}")` : "none"
  );
  transitionLayer.classList.add("is-active");

  let done = false;
  const finish = () => {
    if (done) {
      return;
    }
    done = true;
    sessionStorage.removeItem(TRANSITION_STORAGE_KEY);
    sessionStorage.removeItem(TRANSITION_STORAGE_IMAGE_KEY);
    onComplete();
  };

  transitionLayer.addEventListener(
    "transitionend",
    (e) => {
      if (e.propertyName !== "opacity") {
        return;
      }
      finish();
    },
    { once: true }
  );

  window.setTimeout(finish, TRANSITION_DURATION_MS + 120);
}

export function initTransitionArrival() {
  const transitionLayer = document.querySelector(".page-transition");
  const savedColor = sessionStorage.getItem(TRANSITION_STORAGE_KEY);
  const savedImage = sessionStorage.getItem(TRANSITION_STORAGE_IMAGE_KEY);

  if (!transitionLayer || !savedColor) {
    return;
  }

  const detailPage = document.querySelector("[data-detail-page]");
  const infoPage = document.querySelector("[data-info-page]");
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (detailPage) {
    detailPage.classList.add("is-entering");
  }
  if (infoPage) {
    infoPage.classList.add("is-entering");
  }

  transitionLayer.style.setProperty("--transition-color", savedColor);
  transitionLayer.style.setProperty(
    "--transition-image",
    savedImage ? `url("${savedImage}")` : "none"
  );
  transitionLayer.classList.add("is-active");

  window.requestAnimationFrame(() => {
    window.setTimeout(() => {
      transitionLayer.classList.remove("is-active");
      if (infoPage) {
        infoPage.classList.remove("is-entering");
      }
      sessionStorage.removeItem(TRANSITION_STORAGE_KEY);
      sessionStorage.removeItem(TRANSITION_STORAGE_IMAGE_KEY);
    }, ENTER_REVEAL_DELAY_MS);
  });

  if (detailPage) {
    if (reduceMotion) {
      detailPage.classList.remove("is-entering");
      return;
    }

    window.setTimeout(() => {
      detailPage.classList.remove("is-entering");
    }, DETAIL_TITLE_DELAY_MS);
  }
}

export function initDetailPageEffects() {
  if (activeDetailEffectsController) {
    activeDetailEffectsController.abort();
    activeDetailEffectsController = null;
  }

  const detailPage = document.querySelector("[data-detail-page]");
  if (!detailPage) {
    document.querySelector("[data-service-next]")?.remove();
    removeBrandMask();
    return;
  }

  activeDetailEffectsController = new AbortController();
  const { signal } = activeDetailEffectsController;

  const detailBody = detailPage.querySelector(".detail-body");
  const detailPill = detailPage.querySelector(".detail-pill");
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const rawPath = normalizeServicePath(getCurrentPath());
  const { locale: currentLocale, canonical: currentCanonical } = parseLocalePath(rawPath);
  const currentPath = currentCanonical ?? rawPath;
  const isServiceDetail = SERVICES.some((entry) => entry.path === currentPath);
  const isTeamDetail = detailPage.classList.contains("detail-page--team");
  const isMobileViewport = window.matchMedia("(max-width: 767px)").matches;
  if (isServiceDetail) {
    detailPage.classList.add("detail-page--service");
  }
  if (isServiceDetail) {
    injectNextServiceLink(detailPage);
    if (isMobileViewport) {
      removeBrandMask();
    } else {
      injectBrandMask();
    }
  } else if (isTeamDetail && isMobileViewport) {
    document.querySelector("[data-service-next]")?.remove();
    injectBrandMask();
  } else {
    document.querySelector("[data-service-next]")?.remove();
    removeBrandMask();
  }
  const showImage = () => {
    detailPage.classList.add("is-image-visible");
  };

  if (reduceMotion) {
    showImage();
  } else {
    window.requestAnimationFrame(() => {
      window.setTimeout(showImage, DETAIL_IMAGE_DELAY_MS);
    });
  }

  detailPage.querySelectorAll("[data-scroll-reveal]").forEach((node) => {
    node.classList.add("is-visible");
  });

  if (isMobileViewport) {
    const heroTitle = detailPage.querySelector(".detail-hero__title");
    if (heroTitle && (isServiceDetail || isTeamDetail) && detailBody) {
      heroTitle.style.cursor = "pointer";
      heroTitle.addEventListener("click", () => {
        const bodyTop = detailBody.getBoundingClientRect().top;
        const start = window.scrollY;
        const target = bodyTop + window.scrollY;
        const distance = target - start;
        if (Math.abs(distance) < 2) {
          return;
        }

        const DURATION = 820;
        const OVERSHOOT = 0.07;
        let startTime = null;
        let userInterrupted = false;

        const onUserScroll = () => {
          userInterrupted = true;
        };
        window.addEventListener("wheel", onUserScroll, { passive: true, once: true });
        window.addEventListener("touchstart", onUserScroll, { passive: true, once: true });

        const easeOutBack = (t) => {
          const c1 = OVERSHOOT;
          const c3 = c1 + 1;
          return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
        };

        const step = (timestamp) => {
          if (userInterrupted) {
            return;
          }
          if (!startTime) {
            startTime = timestamp;
          }

          const elapsed = timestamp - startTime;
          const t = Math.min(1, elapsed / DURATION);
          window.scrollTo(0, start + distance * easeOutBack(t));

          if (t < 1) {
            window.requestAnimationFrame(step);
          }
        };

        window.requestAnimationFrame(step);
      }, { signal });
    }

    detailPage.style.setProperty("--detail-content-progress", "1");
    detailPage.style.setProperty("--detail-hero-progress", "1");
    detailPage.style.setProperty("--detail-image-progress", "1");
    return;
  }

  if (detailPage.classList.contains("detail-page--team")) {
    const scrollToTeamContent = (e) => {
      const scroller = detailPage.querySelector("[data-team-horizontal]");
      if (!scroller) {
        return;
      }

      const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      if (maxScroll <= 0) {
        return;
      }

      const DURATION = 820;
      const OVERSHOOT = 0.07;
      let startTime = null;
      let userInterrupted = false;

      const onUserScroll = () => {
        userInterrupted = true;
      };
      scroller.addEventListener("wheel", onUserScroll, { passive: true, once: true });
      scroller.addEventListener("touchstart", onUserScroll, { passive: true, once: true });

      const easeOutBack = (t) => {
        const c1 = OVERSHOOT;
        const c3 = c1 + 1;
        return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
      };

      const step = (timestamp) => {
        if (userInterrupted) {
          return;
        }
        if (!startTime) {
          startTime = timestamp;
        }

        const elapsed = timestamp - startTime;
        const t = Math.min(1, elapsed / DURATION);
        scroller.scrollLeft = maxScroll * easeOutBack(t);

        if (t < 1) {
          window.requestAnimationFrame(step);
        }
      };

      window.requestAnimationFrame(step);
    };

    const heroTitle = detailPage.querySelector(".detail-hero__title");
    if (heroTitle) {
      heroTitle.style.cursor = "pointer";
      document.addEventListener("click", (e) => {
        const introPane = detailPage.querySelector(".team-horizontal__pane--intro");
        if (e.target === introPane || introPane?.contains(e.target)) {
          scrollToTeamContent(e);
        }
      }, { signal });
    }

    // Only initialize horizontal scroll for team if it exists
    if (detailPage.querySelector("[data-team-horizontal]")) {
      initTeamHorizontal(detailPage, reduceMotion, signal);
    }
    // Don't return - continue with scroll effects for equipo
  }

  if (reduceMotion || !detailBody) {
    detailPage.style.setProperty("--detail-content-progress", "1");
    detailPage.style.setProperty("--detail-hero-progress", "0");
    detailPage.style.setProperty("--detail-image-progress", isServiceDetail ? "1" : "0");
    return;
  }

  let ticking = false;
  let initialBodyTop = 0;
  let hasLockedDetailBackground = false;

  const applyStaticProgress = () => {
    detailPage.style.setProperty("--detail-content-progress", "1");
    detailPage.style.setProperty("--detail-hero-progress", "1");
    detailPage.style.setProperty("--detail-image-progress", "1");
    const mask = document.querySelector("[data-brand-mask]");
    if (mask) {
      mask.style.opacity = "1";
    }
  };

  const measureInitialBodyTop = () => {
    initialBodyTop = detailBody.getBoundingClientRect().top;
  };

  const updateDetailScrollProgress = () => {
    const viewportHeight = window.innerHeight;
    const startTop = initialBodyTop;
    // In services, complete the transition when the content actually reaches
    // its landing point below the logo.
    const fadeOutEarlyOffset = isServiceDetail ? 0 : 200;
    const endTop = logoClearance > 0 ? logoClearance - currentSpacerHeight + fadeOutEarlyOffset : viewportHeight * 0.5;
    const bodyTop = detailBody.getBoundingClientRect().top;
    const distance = Math.max(1, startTop - endTop);
    const progress = Math.min(1, Math.max(0, (startTop - bodyTop) / distance));
    const pillTop = detailPill?.getBoundingClientRect().top ?? bodyTop;
    const imageFadeDistance = Math.max(1, viewportHeight * 0.1);
    const topAnchoredImageProgress = Math.min(1, Math.max(0, pillTop / imageFadeDistance));
    const contentDrivenImageProgress = 1 - progress;
    const nextImageProgress = Math.min(topAnchoredImageProgress, contentDrivenImageProgress);

    if (nextImageProgress <= 0.01 || progress >= 0.999) {
      hasLockedDetailBackground = true;
    }

    const imageProgress = isServiceDetail
      ? 1
      : hasLockedDetailBackground
        ? 0
        : nextImageProgress;

    detailPage.style.setProperty("--detail-content-progress", progress.toFixed(3));
    detailPage.style.setProperty("--detail-hero-progress", (1 - progress).toFixed(3));
    detailPage.style.setProperty("--detail-image-progress", imageProgress.toFixed(3));

    const mask = document.querySelector("[data-brand-mask]");
    if (mask) {
      const brand = document.querySelector(".site-brand");
      if (brand) {
        mask.style.height = `${brand.getBoundingClientRect().bottom + 20}px`;
      }
      mask.style.opacity = progress >= 1 ? "1" : "0";
    }
    ticking = false;
  };

  const requestProgressUpdate = () => {
    if (ticking) {
      return;
    }

    ticking = true;
    window.requestAnimationFrame(updateDetailScrollProgress);
  };

  const scrollToContent = () => {
    const bodyTop = detailBody.getBoundingClientRect().top;
    const target = (isTeamDetail && isMobileViewport)
      ? bodyTop + window.scrollY
      : bodyTop + window.scrollY - logoClearance + currentSpacerHeight;
    const start = window.scrollY;
    const distance = target - start;
    if (Math.abs(distance) < 2) {
      return;
    }

    const DURATION = 820;
    const OVERSHOOT = 0.07;
    let startTime = null;
    let userInterrupted = false;

    const onUserScroll = () => {
      userInterrupted = true;
    };
    window.addEventListener("wheel", onUserScroll, { passive: true, once: true });
    window.addEventListener("touchstart", onUserScroll, { passive: true, once: true });

    const easeOutBack = (t) => {
      const c1 = OVERSHOOT;
      const c3 = c1 + 1;
      return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
    };

    const step = (timestamp) => {
      if (userInterrupted) {
        return;
      }
      if (!startTime) {
        startTime = timestamp;
      }

      const elapsed = timestamp - startTime;
      const t = Math.min(1, elapsed / DURATION);
      window.scrollTo(0, start + distance * easeOutBack(t));

      if (t < 1) {
        window.requestAnimationFrame(step);
      }
    };

    window.requestAnimationFrame(step);
  };

  const heroTitle = detailPage.querySelector(".detail-hero__title");
  if (heroTitle && (isServiceDetail || (isTeamDetail && isMobileViewport))) {
    heroTitle.style.cursor = "pointer";
    heroTitle.addEventListener("click", scrollToContent, { signal });
  }

  let logoClearance = 0;
  let currentSpacerHeight = 0;

  const adjustSpacer = () => {
    const brand = document.querySelector(".site-brand");
    const brandRect = brand ? brand.getBoundingClientRect() : null;
    logoClearance = brandRect ? brandRect.bottom + 28 : 160;

    const mask = document.querySelector("[data-brand-mask]");
    if (mask && brandRect) {
      mask.style.height = `${brandRect.bottom + 20}px`;
    }

    if (!detailPill || !isServiceDetail) {
      return;
    }

    const spacer = detailBody.querySelector(".detail-body__spacer");
    if (!spacer) {
      return;
    }

    currentSpacerHeight = logoClearance;

    spacer.style.height = `${currentSpacerHeight}px`;
  };

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      if (signal.aborted) {
        return;
      }

      adjustSpacer();
      measureInitialBodyTop();

      const viewportHeight = window.innerHeight;
      const isScrollable = document.documentElement.scrollHeight > viewportHeight + 2;

      if (!isScrollable) {
        applyStaticProgress();
        return;
      }

      requestProgressUpdate();
      window.addEventListener("scroll", requestProgressUpdate, { passive: true, signal });
      window.addEventListener(
        "resize",
        () => {
          adjustSpacer();
          measureInitialBodyTop();
          requestProgressUpdate();
        },
        { signal }
      );
    });
  });
}

function injectBrandMask() {
  let mask = document.querySelector("[data-brand-mask]");
  if (!mask) {
    mask = document.createElement("div");
    mask.dataset.brandMask = "";
    mask.setAttribute("aria-hidden", "true");
    document.body.append(mask);
  }
  mask.className = "detail-brand-mask";

  // Forzamos propiedades críticas por JS para asegurar que la "función" se cumpla
  Object.assign(mask.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    zIndex: '900', // Por debajo del logo (que debería ser 1000) pero encima del contenido
    backgroundColor: 'var(--detail-brand-mask-color, var(--detail-accent))',
    pointerEvents: 'none',
    transition: 'opacity 0.2s ease-in-out'
  });
}

function removeBrandMask() {
  document.querySelector("[data-brand-mask]")?.remove();
}

function injectNextServiceLink(detailPage) {
  if (detailPage.classList.contains("detail-page--team")) {
    return;
  }

  const rawPath = normalizeServicePath(getCurrentPath());
  const { locale: currentLocale, canonical: currentCanonical } = parseLocalePath(rawPath);
  const canonicalPath = currentCanonical ?? rawPath;
  const currentIndex = SERVICES.findIndex((entry) => entry.path === canonicalPath);

  if (currentIndex < 0) {
    return;
  }

  const nextEntry = SERVICES[(currentIndex + 1) % SERVICES.length];
  const nextSlug = currentLocale === "en" ? nextEntry.hrefEn : nextEntry.href;
  const nextHref = withBase(`/${currentLocale}/${nextSlug}`);
  const nextColor = resolveAccentValue(nextEntry.accentVar);

  const existing = document.querySelector("[data-service-next]");
  const anchor = existing instanceof HTMLAnchorElement ? existing : document.createElement("a");
  anchor.className = "detail-next";
  anchor.href = nextHref;
  anchor.dataset.transitionLink = "";
  anchor.dataset.transitionColor = nextColor;
  anchor.dataset.transitionImage = nextEntry.imageUrl;
  anchor.dataset.serviceNext = "";
  anchor.setAttribute("aria-label", "Siguiente servicio");
  anchor.setAttribute("title", "Siguiente servicio");
  anchor.textContent = "+";

  if (!existing) {
    document.body.append(anchor);
  }
}

let activeDetailEffectsController = null;

function initTeamHorizontal(detailPage, reduceMotion, signal) {
  const scroller = detailPage.querySelector("[data-team-horizontal]");

  if (!scroller) {
    detailPage.style.setProperty("--detail-content-progress", "1");
    detailPage.style.setProperty("--detail-hero-progress", "0");
    return;
  }

  const syncProgress = () => {
    const maxScroll = Math.max(1, scroller.scrollWidth - scroller.clientWidth);
    const rawProgress = maxScroll <= 1 ? 1 : scroller.scrollLeft / maxScroll;
    const progress = Math.min(1, Math.max(0, rawProgress));

    detailPage.style.setProperty("--detail-content-progress", progress.toFixed(3));
    detailPage.style.setProperty("--detail-hero-progress", (1 - progress).toFixed(3));
  };

  if (reduceMotion) {
    detailPage.style.setProperty("--detail-content-progress", "1");
    detailPage.style.setProperty("--detail-hero-progress", "0");
    return;
  }

  const handleWheel = (event) => {
    const intendedDelta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);

    if (!intendedDelta || maxScroll <= 0) {
      return false;
    }

    const isTrackpad = event.deltaMode === WheelEvent.DOM_DELTA_PIXEL && Math.abs(intendedDelta) < 120;
    const multiplier = isTrackpad ? 1.8 : 1.2;
    const appliedDelta = intendedDelta * multiplier;
    const nextScrollLeft = Math.min(maxScroll, Math.max(0, scroller.scrollLeft + appliedDelta));

    if (nextScrollLeft === scroller.scrollLeft) {
      return false;
    }

    event.preventDefault();
    scroller.scrollLeft = nextScrollLeft;
    return true;
  };

  const handleKeydown = (event) => {
    const keySteps = {
      ArrowRight: 120,
      ArrowLeft: -120,
      PageDown: 240,
      PageUp: -240
    };
    const delta = keySteps[event.key];

    if (!delta) {
      return;
    }

    event.preventDefault();
    scroller.scrollBy({ left: delta, behavior: "smooth" });
  };

  syncProgress();
  scroller.addEventListener("scroll", syncProgress, { passive: true, signal });
  scroller.addEventListener("wheel", handleWheel, { passive: false, signal });
  scroller.addEventListener("keydown", handleKeydown, { signal });
  window.addEventListener("resize", syncProgress, { signal });
}
